import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as LucideIcons from 'lucide-react';
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { getTravelHubContent } from '../data/travelHubContent';
import { buildBreadcrumbJsonLd } from '../lib/seo';

/**
 * Shared Travel Hub subpage template.
 *
 * Renders any /travel-hub/:slug route from a single entry in
 * `data/travelHubContent.js`. Adding a new topic = one data entry +
 * optionally one new Route.
 */

const WaIcon = () => (
  <svg className="h-5 w-5 fill-white flex-shrink-0" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const resolveIcon = (name) => (name && LucideIcons[name]) || Sparkles;

/**
 * @param {object} props
 * @param {string} [props.slug] - Optional override; otherwise read from useParams().
 */
const TravelHubSubPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const content = getTravelHubContent(slug);
  const { contactInfo } = useContactInfo();

  if (!content) {
    return <Navigate to="/travel-hub" replace />;
  }

  const waLink = getWhatsAppHref(contactInfo, content.whatsappPrompt);
  const EyebrowIcon = resolveIcon(content.eyebrowIcon);

  const heroImage = content.hero?.image || content.image;
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(
    [
      { name: 'Home', url: '/' },
      { name: 'Travel Hub', url: '/travel-hub' },
      { name: content.title, url: `/travel-hub/${content.slug}` },
    ],
    'https://vietclinics.com',
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`${content.title} | VietClinics Travel Hub`}</title>
        {content.metaDescription && (
          <meta name="description" content={content.metaDescription} />
        )}
        <link rel="canonical" href={`https://vietclinics.com/travel-hub/${content.slug}`} />
        {heroImage && <meta property="og:image" content={heroImage} />}
        {heroImage && <meta name="twitter:image" content={heroImage} />}
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section
        className="bg-gradient-to-r from-[#1E5BA8] to-[#1FA7A1] py-16"
        data-testid={`travel-hub-hero-${content.slug}`}
      >
        <div className="container mx-auto px-4 max-w-3xl">
          <Link
            to="/travel-hub"
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-6 transition-colors"
            data-testid="back-to-travel-hub"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Travel Hub
          </Link>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 mb-5">
            <EyebrowIcon className="h-4 w-4 text-white/80" />
            <span className="text-sm text-white/90 font-medium">{content.eyebrow}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
            {content.title}
          </h1>
          <p className="text-lg text-white/85 max-w-2xl leading-relaxed">
            {content.subtitle}
          </p>
          {content.disclaimer && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white/90">
              <CheckCircle2 className="h-4 w-4 text-white flex-shrink-0" />
              {content.disclaimer}
            </div>
          )}
        </div>
      </section>

      {/* Helps list */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{content.helpsTitle}</h2>
          <ul className="space-y-4">
            {content.helps.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
          {content.helpsFooter && (
            <p className="mt-8 text-sm text-gray-400 italic">{content.helpsFooter}</p>
          )}
        </div>
      </section>

      {/* Audience grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{content.audienceTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {content.audience.map((item, i) => {
              const Icon = resolveIcon(item.icon);
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col items-start gap-3"
                  data-testid={`audience-${i + 1}`}
                >
                  <div className="w-10 h-10 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#1FA7A1]" />
                  </div>
                  <p className="text-sm text-gray-700 font-medium leading-snug">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.steps.map(({ n, text }, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center"
                data-testid={`step-${i + 1}`}
              >
                <div className="w-12 h-12 bg-[#1FA7A1] rounded-full flex items-center justify-center mb-4 shadow-md">
                  <span className="text-white font-bold">{n}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1E5BA8] to-[#1FA7A1]">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">
            {content.ctaTitle}
          </h2>
          <p className="text-white/75 mb-8 text-base">{content.ctaSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#1fb355] shadow-xl transition-all"
                data-testid="cta-whatsapp"
              >
                <WaIcon /> Chat on WhatsApp
              </a>
            )}
            <Link
              to="/#request-appointment"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-[#1FA7A1] font-bold rounded-xl hover:bg-gray-50 shadow-xl transition-all"
              data-testid="cta-contact"
            >
              Request Appointment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TravelHubSubPage;
